import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";
import { curriculumForGrade } from "@/data/curriculum";
import { extractGradeLevel } from "@/lib/profile";

export interface Course {
  id: string;
  name: string;
  icon: string;
  color: string;
  progress: number;
  completedTopics: number;
  totalTopics: number;
  teacher: string;
  lastStudied: string;
  weakCount: number;
  isPriority: boolean;
  hasContent: boolean;
}

export interface Teacher {
  id: string;
  name: string;
  subject: string;
  avatar: string;
  rating: number;
  liveCount: number;
  description: string;
}

export interface VideoLesson {
  videoUrl: string;
  id: string;
  title: string;
  subject: string;
  teacher: string;
  duration: string;
  progress: number;
  thumbnail: string;
}

export interface Question {
  id: number;
  subject: string;
  topic: string;
  difficulty: string;
  type: string;
  question: string;
  options: string[];
  correctIndex: number;
  solution: string;
}

export function useCourses() {
  return useDataQuery<Course[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];

    const { data: auth } = await dataClient.auth.getUser();
    const gradeLevel = extractGradeLevel(String(auth.user?.user_metadata?.sinif ?? ""));
    const [cRes, pRes] = await Promise.all([
      dataClient.from("courses").select("*").order("sort_order"),
      dataClient.from("course_progress").select("*").eq("user_id", userId),
    ]);
    if (cRes.error) throw cRes.error;
    if (pRes.error) throw pRes.error;

    const progMap = new Map(
      (pRes.data ?? []).map((p: { course_id: string }) => [p.course_id, p]),
    );

    const savedCourses = (cRes.data ?? []).map((c: { id: string; name: string; icon: string; color: string; teacher_name: string | null; total_topics: number }) => {
      const p = progMap.get(c.id) as { completed_topics?: number; weak_count?: number; last_studied?: string } | undefined;
      const total = c.total_topics ?? 1;
      const completed = p?.completed_topics ?? 0;
      return {
        id: c.id,
        name: c.name,
        icon: c.icon,
        color: c.color,
        teacher: c.teacher_name ?? "",
        totalTopics: total,
        completedTopics: completed,
        weakCount: p?.weak_count ?? 0,
        lastStudied: p?.last_studied ?? "",
        progress: Math.round((completed / total) * 100),
        isPriority: (p?.weak_count ?? 0) > 0,
        hasContent: true,
      };
    });
    const byName = new Map(savedCourses.map((course) => [course.name.toLocaleLowerCase("tr-TR"), course]));
    const curriculum = curriculumForGrade(gradeLevel);
    const fullList = curriculum.map((subject) => {
      const existing = byName.get(subject.name.toLocaleLowerCase("tr-TR"));
      if (existing) return existing;
      return {
        id: `curriculum-${gradeLevel ?? "general"}-${subject.name}`,
        name: subject.name,
        icon: subject.icon,
        color: subject.color,
        teacher: "",
        totalTopics: 0,
        completedTopics: 0,
        weakCount: 0,
        lastStudied: "",
        progress: 0,
        isPriority: false,
        hasContent: false,
      };
    });
    // Sınıf bilgisi yoksa veya üst sınıflar için müfredat eşlemesi henüz
    // tanımlanmadıysa mevcut katalog davranışını koruruz.
    return fullList.length > 0 ? fullList : savedCourses;
  });
}

export function useTeachers() {
  return useDataQuery<Teacher[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.from("teachers").select("*");
    if (error) throw error;
    return (data ?? []).map((t: { id: string; name: string; subject: string; avatar: string | null; rating: number | null; live_count: number; description: string | null }) => ({
      id: t.id,
      name: t.name,
      subject: t.subject,
      avatar: t.avatar ?? "",
      rating: Number(t.rating ?? 0),
      liveCount: t.live_count ?? 0,
      description: t.description ?? "",
    }));
  });
}

export function useVideoLessons() {
  return useDataQuery<VideoLesson[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];

    const [vRes, pRes] = await Promise.all([
      dataClient.from("video_lessons").select("*").order("sort_order"),
      dataClient.from("video_progress").select("*").eq("user_id", userId),
    ]);
    if (vRes.error) throw vRes.error;
    if (pRes.error) throw pRes.error;

    const progMap = new Map(
      (pRes.data ?? []).map((p: { video_id: string }) => [p.video_id, p]),
    );

    return (vRes.data ?? []).map((v: { id: string; title: string; subject: string; teacher_name: string | null; duration_minutes: number; thumbnail: string | null; video_url?: string }) => {
      const p = progMap.get(v.id) as { progress?: number } | undefined;
      return {
        id: v.id,
        title: v.title,
        subject: v.subject,
        teacher: v.teacher_name ?? "",
        duration: `${v.duration_minutes} dk`,
        progress: p?.progress ?? 0,
        thumbnail: v.thumbnail ?? "",
        videoUrl: v.video_url ?? "",
      };
    });
  });
}

export interface QuestionBank {
  questions: Question[];
  subjects: string[];
  topics: string[];
  difficulties: string[];
  types: string[];
}

function uniqueOf(values: string[]): string[] {
  return ["Tümü", ...Array.from(new Set(values))];
}

export function useQuestions() {
  return useDataQuery<QuestionBank>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient.from("questions").select("*").order("id");
    if (error) throw error;

    const questions: Question[] = (data ?? []).map((q: { id: number; subject: string; topic: string; difficulty: string; qtype: string; question_text: string; options: string[]; correct_index: number; solution: string | null }) => ({
      id: q.id,
      subject: q.subject,
      topic: q.topic,
      difficulty: q.difficulty,
      type: q.qtype,
      question: q.question_text,
      options: q.options ?? [],
      correctIndex: q.correct_index ?? 0,
      solution: q.solution ?? "",
    }));

    return {
      questions,
      subjects: uniqueOf(questions.map((q) => q.subject)),
      topics: uniqueOf(questions.map((q) => q.topic)),
      difficulties: uniqueOf(questions.map((q) => q.difficulty)),
      types: uniqueOf(questions.map((q) => q.type)),
    };
  });
}
